import React, { useEffect, useState } from "react";
import { crmApi } from "../../lib/crmApi";
import { PageHead, Field, inputCls, SERVICE_CATEGORIES, DIGEST_SECTIONS } from "./crmUi";
import InstagramSecrets from "./InstagramSecrets";
import InstagramPanel from "./InstagramPanel";

export default function CrmSettingsPage() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");
  const [waTestTo, setWaTestTo] = useState("");
  const [waBusy, setWaBusy] = useState(false);
  const [waMsg, setWaMsg] = useState("");
  const [waUseTemplate, setWaUseTemplate] = useState(true);
  const [waTemplateName, setWaTemplateName] = useState("hello_world");
  const [waTest, setWaTest] = useState(null);
  const [waVerify, setWaVerify] = useState(null);
  const [waVerifyBusy, setWaVerifyBusy] = useState(false);

  const runWaVerify = async () => {
    setWaVerifyBusy(true); setWaVerify(null);
    try {
      const r = await crmApi.waVerifyConnection();
      setWaVerify(r);
      if (r.sender_number) setS((prev) => ({ ...prev, wa_sender_number: r.sender_number }));
    } catch (e) { setWaVerify({ checks: [{ name: "Verification request", ok: false, detail: e.message }] }); }
    finally { setWaVerifyBusy(false); }
  };

  useEffect(() => {
    crmApi.waTestStatus().then((d) => setWaTest(d.test)).catch(() => {});
  }, []);

  const pollWaStatus = () => {
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const d = await crmApi.waTestStatus();
        setWaTest(d.test);
        if (tries >= 12 || ["delivered", "read", "failed"].includes(d.test?.delivery_status)) clearInterval(t);
      } catch { clearInterval(t); }
    }, 5000);
  };

  const sendWaTest = async () => {
    setWaBusy(true); setWaMsg("");
    try {
      const r = await crmApi.waTestMessage(waTestTo.trim(), waUseTemplate, waTemplateName.trim() || "hello_world");
      setWaTest(r.test);
      if (r.ok) {
        setWaMsg("Accepted by Meta — tracking delivery…");
        pollWaStatus();
      } else {
        setWaMsg(`Failed: ${r.test?.error_reason || r.status}`);
      }
    } catch (e) {
      setWaMsg(`Failed: ${e.message}`);
      crmApi.waTestStatus().then((d) => setWaTest(d.test)).catch(() => {});
    }
    finally { setWaBusy(false); }
  };

  useEffect(() => {
    crmApi.settings().then((d) => setS(d.settings)).catch((e) => setErr(e.message));
  }, []);

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const { id, updated_at, digest_last_sent_date, digest_last_sent_at, digest_last_status, ...body } = s;
      const d = await crmApi.saveSettings(body);
      setS(d.settings);
      setMsg("Settings saved.");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const sendDigestNow = async () => {
    setDigestBusy(true); setDigestMsg("");
    try {
      const r = await crmApi.sendDigestNow();
      const total = Object.values(r.counts || {}).reduce((a, b) => a + b, 0);
      setDigestMsg(`Digest sent to ${r.recipient} — ${total} lead entries across ${Object.keys(r.counts || {}).length} sections.`);
    } catch (e) { setDigestMsg(`Failed: ${e.message}`); }
    finally { setDigestBusy(false); }
  };

  const toggleList = (key, k) =>
    setS((p) => ({
      ...p,
      [key]: (p[key] || []).includes(k) ? p[key].filter((x) => x !== k) : [...(p[key] || []), k],
    }));

  if (!s) return <p className="text-xs text-[#7A6B5F]">{err || "Loading…"}</p>;

  return (
    <div data-testid="crm-settings-page">
      <PageHead eyebrow="Configuration" title="AI Settings">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          data-testid="crm-settings-save"
          className="bg-[#6E1F2C] text-[#FAF5EC] px-5 py-2 text-[10px] tracking-[0.24em] uppercase hover:bg-[#56151F] disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save Settings"}
        </button>
      </PageHead>
      {err && <p className="text-xs text-[#A3141A] mb-2">{err}</p>}
      {msg && <p className="text-xs text-[#3F6E2C] mb-2" data-testid="crm-settings-saved-msg">{msg}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#6E1F2C]/10 p-5 space-y-4">
          <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">Behaviour</p>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="crm-settings-autoreply"
              className="accent-[#6E1F2C] w-4 h-4"
              checked={!!s.ai_auto_reply_active}
              onChange={(e) => setS({ ...s, ai_auto_reply_active: e.target.checked })}
            />
            <span className="text-sm text-[#1C110C]">AI auto-reply active <span className="text-[10px] text-[#7A6B5F]">(takes effect once WhatsApp Cloud API is connected)</span></span>
          </label>
          <Field label="AI response style">
            <select data-testid="crm-ai-style" className={inputCls} value={s.ai_response_style || "concise"} onChange={(e) => setS({ ...s, ai_response_style: e.target.value })}>
              <option value="concise">Concise (default) — 1-2 short sentences</option>
              <option value="balanced">Balanced — 2-3 sentences</option>
              <option value="detailed">Detailed — up to 4-5 sentences</option>
            </select>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Max words / reply">
              <input type="number" min={10} max={200} data-testid="crm-ai-max-words" className={inputCls} value={s.ai_max_words_per_reply ?? 25} onChange={(e) => setS({ ...s, ai_max_words_per_reply: parseInt(e.target.value, 10) || 25 })} />
            </Field>
            <Field label="Max questions / message">
              <input type="number" min={1} max={3} data-testid="crm-ai-max-questions" className={inputCls} value={s.ai_max_questions_per_message ?? 2} onChange={(e) => setS({ ...s, ai_max_questions_per_message: parseInt(e.target.value, 10) || 2 })} />
            </Field>
            <Field label="Max consecutive AI replies (0 = no limit)">
              <input type="number" min={0} max={15} data-testid="crm-ai-max-consecutive" className={inputCls} value={s.ai_max_consecutive_auto_messages ?? 8} onChange={(e) => setS({ ...s, ai_max_consecutive_auto_messages: Number.isNaN(parseInt(e.target.value, 10)) ? 8 : parseInt(e.target.value, 10) })} />
            </Field>
          </div>
          <Field label="Approved trend / market notes (optional — AI may use for suggestions, never as availability promises)">
            <textarea rows={3} data-testid="crm-market-digest" className={inputCls} value={s.market_digest || ""} onChange={(e) => setS({ ...s, market_digest: e.target.value })} placeholder="e.g. Sage green kitchens and fluted wall panels are trending in Dubai villas this season…" />
          </Field>
          <Field label="Working hours"><input className={inputCls} value={s.working_hours || ""} onChange={(e) => setS({ ...s, working_hours: e.target.value })} /></Field>
          <Field label="After-hours message"><textarea rows={2} className={inputCls} value={s.after_hours_message || ""} onChange={(e) => setS({ ...s, after_hours_message: e.target.value })} /></Field>
          <Field label="Default tone"><textarea rows={2} data-testid="crm-settings-tone" className={inputCls} value={s.default_tone || ""} onChange={(e) => setS({ ...s, default_tone: e.target.value })} /></Field>
          <Field label="Human takeover rules"><textarea rows={2} className={inputCls} value={s.human_takeover_rules || ""} onChange={(e) => setS({ ...s, human_takeover_rules: e.target.value })} /></Field>
          <Field label="Follow-up timing rules"><textarea rows={2} className={inputCls} value={s.followup_timing_rules || ""} onChange={(e) => setS({ ...s, followup_timing_rules: e.target.value })} /></Field>
          <Field label="Escalation phone numbers"><input className={inputCls} value={s.escalation_phone_numbers || ""} onChange={(e) => setS({ ...s, escalation_phone_numbers: e.target.value })} placeholder="+9715…, +9715…" /></Field>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-[#6E1F2C]/10 p-5 space-y-4" data-testid="crm-digest-settings">
            <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">Daily Follow-up Digest Email</p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="crm-digest-enabled"
                className="accent-[#6E1F2C] w-4 h-4"
                checked={s.digest_enabled !== false}
                onChange={(e) => setS({ ...s, digest_enabled: e.target.checked })}
              />
              <span className="text-sm text-[#1C110C]">Send a daily digest every morning</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Recipient email">
                <input data-testid="crm-digest-recipient" className={inputCls} value={s.digest_recipient || ""} onChange={(e) => setS({ ...s, digest_recipient: e.target.value })} placeholder="sales@timefurniture.ae" />
              </Field>
              <Field label="CC (optional, comma-separated)">
                <input data-testid="crm-digest-cc" className={inputCls} value={s.digest_cc || ""} onChange={(e) => setS({ ...s, digest_cc: e.target.value })} placeholder="manager@…" />
              </Field>
              <Field label="Send time (UAE / GST)">
                <input type="time" data-testid="crm-digest-time" className={inputCls} value={s.digest_send_time || "08:30"} onChange={(e) => setS({ ...s, digest_send_time: e.target.value })} />
              </Field>
            </div>
            <div>
              <p className="text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F] mb-2">Sections to include</p>
              <div className="flex flex-wrap gap-1.5">
                {DIGEST_SECTIONS.map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`crm-digest-section-${k}`}
                    onClick={() => toggleList("digest_sections", k)}
                    className={`text-[9px] tracking-[0.12em] uppercase px-2 py-1 border ${
                      (s.digest_sections || []).includes(k) ? "bg-[#6E1F2C] text-white border-[#6E1F2C]" : "border-[#6E1F2C]/25 text-[#6E1F2C]"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap pt-1 border-t border-[#6E1F2C]/10">
              <button
                type="button"
                onClick={sendDigestNow}
                disabled={digestBusy}
                data-testid="crm-digest-send-now"
                className="mt-3 border border-[#6E1F2C] text-[#6E1F2C] px-4 py-2 text-[9px] tracking-[0.22em] uppercase hover:bg-[#6E1F2C] hover:text-white disabled:opacity-60"
              >
                {digestBusy ? "Sending…" : "Send test digest now"}
              </button>
              {s.digest_last_status && (
                <span className="mt-3 text-[10px] text-[#7A6B5F]">
                  Last: {s.digest_last_status}{s.digest_last_sent_at ? ` · ${(s.digest_last_sent_at || "").slice(0, 16).replace("T", " ")}` : ""}
                </span>
              )}
            </div>
            {digestMsg && (
              <p className={`text-xs ${digestMsg.startsWith("Failed") ? "text-[#A3141A]" : "text-[#3F6E2C]"}`} data-testid="crm-digest-result">
                {digestMsg}
              </p>
            )}
          </div>

          <div className="bg-white border border-[#6E1F2C]/10 p-5">
            <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] mb-3">Services AI can handle</p>
            <div className="flex flex-wrap gap-1.5">
              {SERVICE_CATEGORIES.map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleList("services_ai_can_handle", k)}
                  className={`text-[9px] tracking-[0.12em] uppercase px-2 py-1 border ${
                    (s.services_ai_can_handle || []).includes(k) ? "bg-[#3F6E2C] text-white border-[#3F6E2C]" : "border-[#6E1F2C]/25 text-[#6E1F2C]"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] mb-3 mt-5">Services requiring human approval</p>
            <div className="flex flex-wrap gap-1.5">
              {SERVICE_CATEGORIES.map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleList("services_require_human", k)}
                  className={`text-[9px] tracking-[0.12em] uppercase px-2 py-1 border ${
                    (s.services_require_human || []).includes(k) ? "bg-[#A3141A] text-white border-[#A3141A]" : "border-[#6E1F2C]/25 text-[#6E1F2C]"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border-2 border-[#D4A963] p-5 space-y-4" data-testid="crm-temp-wa-settings">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C]">Temporary WhatsApp Routing</p>
              <span className={`text-[9px] tracking-[0.16em] uppercase px-2 py-1 ${s.temp_wa_active ? "bg-[#D4A963]/20 text-[#A6822F] border border-[#D4A963]/50" : "bg-[#3F6E2C]/10 text-[#3F6E2C] border border-[#3F6E2C]/30"}`} data-testid="crm-temp-wa-badge">
                {s.temp_wa_active ? "Active — routing to temporary number" : "Off — routing to main number"}
              </span>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="crm-temp-wa-active"
                className="accent-[#6E1F2C] w-4 h-4"
                checked={!!s.temp_wa_active}
                onChange={(e) => setS({ ...s, temp_wa_active: e.target.checked })}
              />
              <span className="text-sm text-[#1C110C]">Temporary WhatsApp Routing Active</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Temporary WhatsApp Number">
                <input data-testid="crm-temp-wa-number" className={inputCls} value={s.temp_wa_number || ""} onChange={(e) => setS({ ...s, temp_wa_number: e.target.value })} placeholder="+971543068992" />
              </Field>
              <Field label="Public Website WhatsApp Number (CRM number)">
                <input data-testid="crm-temp-wa-main" className={inputCls} value={s.main_wa_number || ""} onChange={(e) => setS({ ...s, main_wa_number: e.target.value })} placeholder="+97165530401" />
              </Field>
              <Field label="Internal Sales Alert WhatsApp Number">
                <input data-testid="crm-internal-alert-number" className={inputCls} value={s.internal_alert_wa_number || ""} onChange={(e) => setS({ ...s, internal_alert_wa_number: e.target.value })} placeholder="+97165591935" />
              </Field>
              <Field label="Fallback Admin Number">
                <input data-testid="crm-fallback-admin-number" className={inputCls} value={s.wa_fallback_admin_number || ""} onChange={(e) => setS({ ...s, wa_fallback_admin_number: e.target.value })} placeholder="+971551110223" />
              </Field>
            </div>
            <Field label="Temporary Notice Text (shown on the website while active)">
              <textarea rows={3} data-testid="crm-temp-wa-notice" className={inputCls} value={s.temp_wa_notice || ""} onChange={(e) => setS({ ...s, temp_wa_notice: e.target.value })} />
            </Field>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={save}
                disabled={busy}
                data-testid="crm-temp-wa-save"
                className="bg-[#6E1F2C] text-[#FAF5EC] px-5 py-2.5 text-[10px] tracking-[0.24em] uppercase hover:bg-[#56151F] disabled:opacity-60"
              >
                {busy ? "Saving…" : "Save Routing"}
              </button>
              {msg && <span className="text-[11px] text-[#3F6E2C]">{msg}</span>}
              {s.temp_wa_updated_at && (
                <span className="text-[10px] text-[#7A6B5F]">Last updated: {(s.temp_wa_updated_at || "").slice(0, 16).replace("T", " ")}</span>
              )}
            </div>
            <p className="text-[10px] text-[#7A6B5F] leading-relaxed">
              Public website buttons route to the <strong>Public Website WhatsApp Number</strong> ({s.main_wa_number || "+97165530401"}). The <strong>Internal Sales Alert Number</strong> ({s.internal_alert_wa_number || "+97165591935"}) only receives qualified-lead alerts from the CRM — it is never shown on the public website. Keep temporary routing OFF unless the CRM number is down.
            </p>
          </div>

          <div className="bg-[#1C110C] p-5 space-y-4" data-testid="crm-wa-settings">
            <p className="text-[10px] tracking-[0.26em] uppercase text-[#D4A963]">WhatsApp Cloud API</p>
            {/* Setup checklist */}
            <div className="bg-white/5 border border-white/10 p-3 space-y-1.5" data-testid="crm-wa-checklist">
              <p className="text-[9px] tracking-[0.22em] uppercase text-[#EDE1C8]/60 mb-1">Setup checklist</p>
              {[
                ["Meta Business Portfolio approval", (s.wa_meta_approval_status || "pending").charAt(0).toUpperCase() + (s.wa_meta_approval_status || "pending").slice(1), s.wa_meta_approval_status === "approved"],
                ["WhatsApp Business Account ID", s.wa_business_account_id ? "Saved" : "Pending", !!s.wa_business_account_id],
                ["Phone Number ID", s.wa_phone_number_id ? "Saved" : "Pending", !!s.wa_phone_number_id],
                ["Access Token", s.wa_access_token_set ? "Saved" : "Pending", !!s.wa_access_token_set],
                ["Webhook Verify Token", s.wa_verify_token ? "Generated internally" : "Pending", !!s.wa_verify_token],
                ["Webhook verification", s.wa_webhook_connected ? "Connected" : "Pending", !!s.wa_webhook_connected],
                ["Test message", s.wa_last_test_message_status ? s.wa_last_test_message_status.slice(0, 40) : "Pending", (s.wa_last_test_message_status || "").startsWith("sent") || (s.wa_last_test_message_status || "").startsWith("accepted") || (s.wa_last_test_message_status || "").startsWith("delivered")],
                ["Current CRM Sender Number", s.wa_sender_number || "Unknown — click Verify Connection", !!s.wa_sender_number],
              ].map(([lbl, val, ok]) => (
                <div key={lbl} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="text-[#EDE1C8]/70">{ok ? "✅" : "⭕"} {lbl}</span>
                  <span className={ok ? "text-[#7FBF5F]" : "text-[#D4A963]"}>{val}</span>
                </div>
              ))}
            </div>
            <Field label="Meta Business Portfolio approval status">
              <select data-testid="crm-wa-approval-status" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8] [&>option]:text-black`} value={s.wa_meta_approval_status || "pending"} onChange={(e) => setS({ ...s, wa_meta_approval_status: e.target.value })}>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="restricted">Restricted</option>
              </select>
            </Field>
            <p className="text-[11px] text-[#D4A963]/90 leading-relaxed border border-[#D4A963]/30 p-2.5" data-testid="crm-wa-instructions">
              Once Meta Business Portfolio is approved, go to <strong>Meta Developers → App → WhatsApp → API Setup</strong> and paste the WhatsApp Business Account ID, Phone Number ID, and Access Token here.
            </p>
            <div className="flex items-center gap-3 flex-wrap text-[11px]">
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 border ${s.wa_webhook_connected ? "border-[#3F6E2C] text-[#7FBF5F]" : "border-[#A3141A]/60 text-[#E08080]"}`} data-testid="crm-wa-status">
                ● {s.wa_webhook_connected ? "Webhook Connected" : "Not Connected"}
              </span>
              {s.wa_last_webhook_at && (
                <span className="text-[#EDE1C8]/50">Last event: {(s.wa_last_webhook_at || "").slice(0, 16).replace("T", " ")}</span>
              )}
              {s.wa_last_test_message_status && (
                <span className="text-[#EDE1C8]/50">Test msg: {s.wa_last_test_message_status}</span>
              )}
            </div>
            <Field label="Webhook Callback URL (paste in Meta Developers)">
              <div className="flex gap-2">
                <input readOnly data-testid="crm-wa-webhook-url" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={`${window.location.origin}/api/crm/whatsapp/webhook`} onFocus={(e) => e.target.select()} />
                <button type="button" onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/api/crm/whatsapp/webhook`)} className="border border-[#D4A963]/50 text-[#D4A963] px-3 text-[9px] tracking-[0.16em] uppercase hover:bg-[#D4A963] hover:text-[#1C110C]">Copy</button>
              </div>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="WhatsApp Business Account ID">
                <input data-testid="crm-wa-waba" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_business_account_id || ""} onChange={(e) => setS({ ...s, wa_business_account_id: e.target.value })} />
              </Field>
              <Field label="Phone Number ID">
                <input data-testid="crm-wa-pnid" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_phone_number_id || ""} onChange={(e) => setS({ ...s, wa_phone_number_id: e.target.value })} />
              </Field>
              <Field label={`Permanent Access Token${s.wa_access_token_set ? " (saved)" : ""}`}>
                <input type="password" data-testid="crm-wa-token" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_access_token || ""} onChange={(e) => setS({ ...s, wa_access_token: e.target.value })} placeholder={s.wa_access_token_set ? "•••• saved — type to replace" : "EAAG…"} />
              </Field>
              <Field label="Webhook Verify Token (you invent this)">
                <input data-testid="crm-wa-verify" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_verify_token || ""} onChange={(e) => setS({ ...s, wa_verify_token: e.target.value })} placeholder="e.g. timefurniture-verify-2026" />
              </Field>
              <Field label={`App Secret${s.wa_app_secret_set ? " (saved)" : ""}`}>
                <input type="password" data-testid="crm-wa-secret" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_app_secret || ""} onChange={(e) => setS({ ...s, wa_app_secret: e.target.value })} placeholder={s.wa_app_secret_set ? "•••• saved — type to replace" : "from Meta App → Settings → Basic"} />
              </Field>
              <Field label="Auto Reply Mode">
                <select data-testid="crm-wa-automode" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8] [&>option]:text-black`} value={s.auto_reply_mode || "draft_only"} onChange={(e) => setS({ ...s, auto_reply_mode: e.target.value })}>
                  <option value="off">Off</option>
                  <option value="draft_only">Draft Only (default)</option>
                  <option value="basic_qualification" disabled={!(s.wa_phone_number_id && s.wa_access_token_set && s.wa_webhook_connected)}>Auto-reply: basic qualification only</option>
                  <option value="full_auto" disabled={!(s.wa_phone_number_id && s.wa_access_token_set && s.wa_webhook_connected)}>Full Auto Reply</option>
                </select>
                {!(s.wa_phone_number_id && s.wa_access_token_set && s.wa_webhook_connected) && (
                  <p className="text-[10px] text-[#EDE1C8]/50 mt-1">Auto-reply unlocks after credentials are connected &amp; webhook verified.</p>
                )}
              </Field>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                  <input type="checkbox" data-testid="crm-wa-approval" className="accent-[#D4A963] w-4 h-4" checked={!!s.human_approval_required} onChange={(e) => setS({ ...s, human_approval_required: e.target.checked })} />
                  Human approval required (ignored in Full Auto — risky cases always escalate)
                </label>
              </div>
            </div>
            <div className="border-t border-white/10 pt-3 space-y-2" data-testid="crm-knowledge-settings">
              <p className="text-[9px] tracking-[0.22em] uppercase text-[#EDE1C8]/60">Knowledge Engine</p>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-kb-retrieval" className="accent-[#D4A963] w-4 h-4" checked={!!s.kb_retrieval_enabled} onChange={(e) => setS({ ...s, kb_retrieval_enabled: e.target.checked })} />
                Knowledge retrieval (RAG) — the AI answers using approved KB articles
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-ref-sharing" className="accent-[#D4A963] w-4 h-4" checked={!!s.reference_sharing_enabled} onChange={(e) => setS({ ...s, reference_sharing_enabled: e.target.checked })} />
                AI reference sharing — auto-send 1–3 approved library references when customers ask for examples
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-auto-assign" className="accent-[#D4A963] w-4 h-4" checked={s.auto_assign_enabled !== false} onChange={(e) => setS({ ...s, auto_assign_enabled: e.target.checked })} />
                Intelligent lead assignment — auto-assign qualified leads (specialty match + lowest workload)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-followup-auto" className="accent-[#D4A963] w-4 h-4" checked={!!s.followup_automation_enabled} onChange={(e) => setS({ ...s, followup_automation_enabled: e.target.checked })} />
                Automated follow-up sequences — auto-send inside the 24h window, draft + task outside it
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-visual-gen" className="accent-[#D4A963] w-4 h-4" checked={!!s.visual_gen_enabled} onChange={(e) => setS({ ...s, visual_gen_enabled: e.target.checked })} />
                AI concept prompts — AI suggests an image prompt when a customer asks for a visual (staff review, generate &amp; send)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-visual-autosend" className="accent-[#D4A963] w-4 h-4" checked={!!s.visual_auto_send_enabled} onChange={(e) => setS({ ...s, visual_auto_send_enabled: e.target.checked })} />
                ⚠ Full auto-send — AI generates AND sends concepts without staff approval (not recommended)
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                <Field label="Monthly image budget (USD)">
                  <input type="number" min="0" step="1" data-testid="crm-visual-budget" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.visual_monthly_budget_usd ?? 25} onChange={(e) => setS({ ...s, visual_monthly_budget_usd: Number(e.target.value) })} />
                </Field>
                <Field label="Est. cost per image (USD)">
                  <input type="number" min="0" step="0.01" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.visual_cost_per_image_usd ?? 0.17} onChange={(e) => setS({ ...s, visual_cost_per_image_usd: Number(e.target.value) })} />
                </Field>
                <Field label="Max images per request">
                  <input type="number" min="1" max="4" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.visual_max_images_per_request ?? 3} onChange={(e) => setS({ ...s, visual_max_images_per_request: Number(e.target.value) })} />
                </Field>
              </div>
              <p className="text-[9px] tracking-[0.22em] uppercase text-[#EDE1C8]/60 pt-2">Photorealistic Quality Standard</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Photorealism quality">
                  <select data-testid="crm-visual-realism" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.visual_photorealism_quality || "maximum"} onChange={(e) => setS({ ...s, visual_photorealism_quality: e.target.value })}>
                    <option value="standard" className="text-black">Standard</option>
                    <option value="high" className="text-black">High</option>
                    <option value="maximum" className="text-black">Maximum</option>
                  </select>
                </Field>
                <Field label="Output resolution / detail">
                  <select data-testid="crm-visual-resolution" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.visual_output_quality || "high"} onChange={(e) => setS({ ...s, visual_output_quality: e.target.value })}>
                    <option value="low" className="text-black">Standard (fastest, cheapest)</option>
                    <option value="medium" className="text-black">High</option>
                    <option value="high" className="text-black">Maximum (best detail)</option>
                  </select>
                </Field>
                <Field label="Max auto-regeneration attempts">
                  <input type="number" min="0" max="3" data-testid="crm-visual-regen-max" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.visual_max_regen_attempts ?? 2} onChange={(e) => setS({ ...s, visual_max_regen_attempts: Number(e.target.value) })} />
                </Field>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-visual-qc" className="accent-[#D4A963] w-4 h-4" checked={s.visual_quality_review_enabled !== false} onChange={(e) => setS({ ...s, visual_quality_review_enabled: e.target.checked })} />
                Automatic AI quality review — score realism, geometry, materials &amp; brief match before staff see the image
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-visual-autoregen" className="accent-[#D4A963] w-4 h-4" checked={s.visual_auto_regen_enabled !== false} onChange={(e) => setS({ ...s, visual_auto_regen_enabled: e.target.checked })} />
                Auto-regenerate images that fail the quality check (budget-aware, capped by max attempts)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-visual-people" className="accent-[#D4A963] w-4 h-4" checked={!!s.visual_people_allowed} onChange={(e) => setS({ ...s, visual_people_allowed: e.target.checked })} />
                Allow people in generated visuals (off = never include people)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-visual-approval" className="accent-[#D4A963] w-4 h-4" checked={s.visual_staff_approval_required !== false} onChange={(e) => setS({ ...s, visual_staff_approval_required: e.target.checked })} />
                Staff approval required before any concept reaches a customer (recommended ON)
              </label>
            </div>
            <div className="border-t border-white/10 pt-3 space-y-3" data-testid="crm-wa-alert-settings">
              <p className="text-[9px] tracking-[0.22em] uppercase text-[#EDE1C8]/60">Internal Lead Alerts → {s.internal_alert_wa_number || "+97165591935"}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Approved alert template name">
                  <input data-testid="crm-wa-alert-template" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_alert_template_name || ""} onChange={(e) => setS({ ...s, wa_alert_template_name: e.target.value })} placeholder="new_lead_alert" />
                </Field>
                <Field label="Template language code">
                  <input className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_alert_template_lang || "en"} onChange={(e) => setS({ ...s, wa_alert_template_lang: e.target.value })} placeholder="en" />
                </Field>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-wa-alert-template-ready" className="accent-[#D4A963] w-4 h-4" checked={!!s.wa_alert_template_ready} onChange={(e) => setS({ ...s, wa_alert_template_ready: e.target.checked })} />
                Template approved by Meta — use it for alerts (works outside the 24-hour window)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-[#EDE1C8] text-xs">
                <input type="checkbox" data-testid="crm-wa-alert-email-fallback" className="accent-[#D4A963] w-4 h-4" checked={s.wa_alert_email_fallback !== false} onChange={(e) => setS({ ...s, wa_alert_email_fallback: e.target.checked })} />
                Email fallback if WhatsApp alert fails (sends to {s.digest_recipient || "sales@timefurniture.ae"})
              </label>
              <p className="text-[10px] text-[#D4A963]/80 leading-relaxed">
                Alert order: approved template → plain text (needs the alert number to have messaged the CRM number within 24h) → email fallback. Until <strong>new_lead_alert</strong> is approved in Meta Business Manager, keep the internal number in a 24h window by sending any message from {s.internal_alert_wa_number || "+97165591935"} to {s.wa_sender_number || "the CRM number"} daily, or rely on email.
              </p>
            </div>
            <div className="border-t border-white/10 pt-3 space-y-3">
              <p className="text-[9px] tracking-[0.22em] uppercase text-[#EDE1C8]/60">Customer-Turn Batching (one AI reply per burst of messages/photos)</p>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <Field label="Text quiet (sec)">
                  <input type="number" step="0.5" min={1} max={60} data-testid="crm-wa-batch-quiet" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_batch_quiet_seconds ?? 2.5} onChange={(e) => setS({ ...s, wa_batch_quiet_seconds: parseFloat(e.target.value) || 2.5 })} />
                </Field>
                <Field label="Text max wait (sec)">
                  <input type="number" step="0.5" min={2} max={120} data-testid="crm-wa-batch-maxwait" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_batch_max_wait_seconds ?? 5} onChange={(e) => setS({ ...s, wa_batch_max_wait_seconds: parseFloat(e.target.value) || 5 })} />
                </Field>
                <Field label="Media quiet (sec)">
                  <input type="number" step="0.5" min={1} max={60} data-testid="crm-wa-batch-quiet-media" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_batch_quiet_media_seconds ?? 5} onChange={(e) => setS({ ...s, wa_batch_quiet_media_seconds: parseFloat(e.target.value) || 5 })} />
                </Field>
                <Field label="Media max wait (sec)">
                  <input type="number" step="0.5" min={2} max={120} data-testid="crm-wa-batch-maxwait-media" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_batch_max_wait_media_seconds ?? 8} onChange={(e) => setS({ ...s, wa_batch_max_wait_media_seconds: parseFloat(e.target.value) || 8 })} />
                </Field>
                <Field label="Max images / AI batch">
                  <input type="number" min={1} max={10} data-testid="crm-wa-batch-maximages" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8]`} value={s.wa_batch_max_images ?? 6} onChange={(e) => setS({ ...s, wa_batch_max_images: parseInt(e.target.value, 10) || 6 })} />
                </Field>
              </div>
            </div>
            <div className="border-t border-white/10 pt-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={runWaVerify}
                  disabled={waVerifyBusy}
                  data-testid="crm-wa-verify-btn"
                  className="border border-[#7FBF5F]/60 text-[#7FBF5F] px-4 py-2 text-[9px] tracking-[0.2em] uppercase hover:bg-[#7FBF5F] hover:text-[#1C110C] disabled:opacity-50"
                >
                  {waVerifyBusy ? "Checking…" : "Verify Connection (token · Phone Number ID · WABA)"}
                </button>
                {s.wa_sender_number && (
                  <span className="text-[11px] text-[#7FBF5F]" data-testid="crm-wa-sender-number">
                    CRM sends from: <strong>{s.wa_sender_number}</strong>
                  </span>
                )}
              </div>
              {waVerify && (
                <div className="bg-white/5 border border-white/10 p-3 space-y-1.5" data-testid="crm-wa-verify-results">
                  {(waVerify.checks || []).map((c) => (
                    <div key={c.name} className="text-[11px] flex gap-2">
                      <span>{c.ok ? "✅" : "❌"}</span>
                      <span className="text-[#EDE1C8]/80"><strong>{c.name}:</strong> <span className={c.ok ? "text-[#7FBF5F]" : "text-[#E08080]"}>{c.detail}</span></span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <input data-testid="crm-wa-test-to" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8] max-w-[200px]`} value={waTestTo} onChange={(e) => setWaTestTo(e.target.value)} placeholder="+9715… test number" />
                <label className="flex items-center gap-1.5 text-[#EDE1C8] text-[11px] cursor-pointer">
                  <input type="checkbox" data-testid="crm-wa-test-template" className="accent-[#D4A963] w-3.5 h-3.5" checked={waUseTemplate} onChange={(e) => setWaUseTemplate(e.target.checked)} />
                  Send as template
                </label>
                {waUseTemplate && (
                  <input data-testid="crm-wa-test-template-name" className={`${inputCls} bg-white/10 border-white/20 text-[#EDE1C8] max-w-[140px]`} value={waTemplateName} onChange={(e) => setWaTemplateName(e.target.value)} placeholder="hello_world" />
                )}
                <button
                  type="button"
                  onClick={sendWaTest}
                  disabled={waBusy || !waTestTo}
                  data-testid="crm-wa-test-btn"
                  className="border border-[#D4A963]/50 text-[#D4A963] px-4 py-2 text-[9px] tracking-[0.2em] uppercase hover:bg-[#D4A963] hover:text-[#1C110C] disabled:opacity-50"
                >
                  {waBusy ? "Sending…" : "Send test WhatsApp message"}
                </button>
                {waMsg && <span className={`text-[11px] ${waMsg.startsWith("Failed") ? "text-[#E08080]" : "text-[#7FBF5F]"}`} data-testid="crm-wa-test-result">{waMsg}</span>}
              </div>
              <p className="text-[10px] text-[#D4A963]/80">
                Plain-text tests only deliver if the recipient messaged your business number within the last 24 hours (Meta's customer-service window) — Meta still returns a message ID but never delivers it. Use the approved <strong>hello_world</strong> template (default) to test outside the window.
              </p>
              {waTest && (
                <div className="bg-white/5 border border-white/10 p-3 space-y-1" data-testid="crm-wa-test-status">
                  <p className="text-[9px] tracking-[0.22em] uppercase text-[#EDE1C8]/60 mb-1">Last test message</p>
                  {[
                    ["Recipient", waTest.to],
                    ["Type", waTest.kind === "template" ? `template (${waTest.template_name})` : "free-form text"],
                    ["Sender Phone Number ID used", waTest.pnid_used || "—"],
                    ["Message ID", waTest.wa_message_id || "—"],
                    ["API accepted", waTest.api_accepted ? "Yes" : "No"],
                    ["Delivery status", waTest.delivery_status || (waTest.api_accepted ? "accepted — awaiting webhook status" : "—")],
                    ["Last status update", waTest.last_status_at ? waTest.last_status_at.slice(0, 19).replace("T", " ") : "—"],
                    ["Sent at", waTest.sent_at ? waTest.sent_at.slice(0, 19).replace("T", " ") : "—"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 text-[11px]">
                      <span className="text-[#EDE1C8]/60">{k}</span>
                      <span className={`text-right break-all ${k === "Delivery status" && waTest.delivery_status === "failed" ? "text-[#E08080]" : waTest.delivery_status === "delivered" || waTest.delivery_status === "read" ? (k === "Delivery status" ? "text-[#7FBF5F]" : "text-[#EDE1C8]") : "text-[#EDE1C8]"}`}>{v}</span>
                    </div>
                  ))}
                  {waTest.error_reason && (
                    <p className="text-[11px] text-[#E08080] pt-1 border-t border-white/10" data-testid="crm-wa-test-error">
                      Meta error: {waTest.error_reason}
                    </p>
                  )}
                  {waTest.error_details && (
                    <div className="text-[10px] text-[#E08080]/90 space-y-0.5" data-testid="crm-wa-test-error-details">
                      <p>HTTP status: {waTest.error_details.http_status ?? "—"} · Meta code: {waTest.error_details.code ?? "—"}{waTest.error_details.error_subcode ? ` · subcode: ${waTest.error_details.error_subcode}` : ""}</p>
                      <p>Message: {waTest.error_details.message}</p>
                      {waTest.error_details.fbtrace_id && <p>Trace ID: {waTest.error_details.fbtrace_id}</p>}
                    </div>
                  )}
                  {waTest.diagnosis && (
                    <p className="text-[11px] text-[#D4A963] pt-1" data-testid="crm-wa-test-diagnosis">
                      Diagnosis: {waTest.diagnosis}
                    </p>
                  )}
                  <button type="button" onClick={() => crmApi.waTestStatus().then((d) => setWaTest(d.test)).catch(() => {})} data-testid="crm-wa-test-refresh" className="text-[9px] tracking-[0.18em] uppercase text-[#D4A963] hover:text-white mt-1">
                    ↻ Refresh status
                  </button>
                </div>
              )}
            </div>
            <div className="border-t border-white/10 pt-4 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={save}
                disabled={busy}
                data-testid="crm-wa-save-btn"
                className="bg-[#D4A963] text-[#1C110C] px-6 py-2.5 text-[10px] tracking-[0.24em] uppercase font-medium hover:bg-[#c49850] disabled:opacity-60"
              >
                {busy ? "Saving…" : "Save WhatsApp Settings"}
              </button>
              {msg && <span className="text-[11px] text-[#7FBF5F]" data-testid="crm-wa-saved-msg">{msg}</span>}
              {err && <span className="text-[11px] text-[#E08080]">{err}</span>}
            </div>
            <p className="text-[10px] text-[#EDE1C8]/50 leading-relaxed">
              Fields are not auto-saved — click "Save WhatsApp Settings" above (or the main Save button at the top). In Full Auto mode the AI replies to all safe qualification messages automatically, but ALWAYS hands over to a human for: final pricing, discounts, complaints, manager requests, payment/contract terms, angry customers, low-confidence AI, and qualified renovation/fit-out projects.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 max-w-2xl space-y-6">
        <InstagramSecrets />
        <InstagramPanel />
      </div>
    </div>
  );
}
